/**
 * Google Maps JavaScript API yukleyicisi.
 *
 * Yalnizca kullanilan alt kume tiplenir; @types/google.maps bagimliligi eklenmez.
 * Betik sayfaya bir kez eklenir ve anahtari/dili sonradan degistirilemez. Anahtar
 * gecersiz/kisitli ise Google haritayi yukledikten SONRA window.gm_authFailure() cagirir;
 * bu da dinleyicilere iletilir.
 */
export type LatLngLiteral = { lat: number; lng: number };

export interface GoogleLatLng {
  lat(): number;
  lng(): number;
}

export interface GooglePoint {
  x: number;
  y: number;
}

export interface GoogleMapCanvasProjection {
  /** Koordinati, haritayla birlikte kayan katman (pane) pikseline cevirir */
  fromLatLngToDivPixel(latLng: GoogleLatLng): GooglePoint | null;
}

export interface GoogleMapPanes {
  floatPane: Element;
  overlayMouseTarget: Element;
}

export interface GoogleMap {
  setCenter(center: LatLngLiteral): void;
  setZoom(zoom: number): void;
}

export interface GoogleOverlayView {
  onAdd?: () => void;
  draw?: () => void;
  onRemove?: () => void;
  setMap(map: GoogleMap | null): void;
  getPanes(): GoogleMapPanes | null;
  getProjection(): GoogleMapCanvasProjection | null;
}

export interface GoogleMapOptions {
  center: LatLngLiteral;
  zoom: number;
  mapTypeId?: 'roadmap' | 'satellite' | 'hybrid' | 'terrain';
  mapTypeControl?: boolean;
  /** position: google.maps.ControlPosition degeri (ornegin LEFT_BOTTOM) */
  mapTypeControlOptions?: { position?: number };
  streetViewControl?: boolean;
  fullscreenControl?: boolean;
  zoomControl?: boolean;
  zoomControlOptions?: { position?: number };
  /** Yeni "kamera kontrolu" (zoomControl'un yerini alir); kapali tutulur, klasik +/- kullanilir */
  cameraControl?: boolean;
  gestureHandling?: 'cooperative' | 'greedy' | 'none' | 'auto';
}

export interface GoogleMapsApi {
  Map: new (container: HTMLElement, options: GoogleMapOptions) => GoogleMap;
  LatLng: new (lat: number, lng: number) => GoogleLatLng;
  OverlayView: new () => GoogleOverlayView;
  /** Kontrol kose sabitleri (LEFT_BOTTOM, RIGHT_BOTTOM, ...); eski API surumlerinde olmayabilir */
  ControlPosition?: Record<string, number>;
}

type GoogleWindow = Window & {
  google?: { maps?: Partial<GoogleMapsApi> };
  gm_authFailure?: () => void;
  [key: string]: unknown;
};

const CALLBACK_NAME = '__tbhPortGoogleMapsReady';
export const GOOGLE_MAPS_SCRIPT_HOST = 'https://maps.googleapis.com/maps/api/js';
/**
 * maps/api/js yalnizca kucuk bir onyukleyicidir; asil main.js'i o ekler ve callback oradan
 * cagrilir. main.js gelmezse (ag kesintisi, filtre) onerror tetiklenmez; promise askida
 * kalmasin diye zaman asimi vardir.
 */
export const GOOGLE_MAPS_LOAD_TIMEOUT_MS = 20000;

let pending: Promise<GoogleMapsApi> | null = null;
let requestedKey: string | null = null;
let authFailed = false;
let authHookInstalled = false;
const authFailureListeners = new Set<() => void>();

function win(): GoogleWindow {
  return window as unknown as GoogleWindow;
}

function loadedApi(): GoogleMapsApi | null {
  const maps = win().google?.maps;
  return maps && maps.Map && maps.OverlayView && maps.LatLng ? (maps as GoogleMapsApi) : null;
}

function warnKeyMismatch(apiKey: string) {
  if (requestedKey !== null && apiKey !== requestedKey) {
    // Betik ikinci kez yuklenemez: farkli anahtarla cagri sessizce eskisini kullanirdi
    console.warn(
      'Google Maps API zaten baska bir anahtarla yuklendi; yeni anahtar sayfa yenilenene kadar kullanilamaz.',
    );
  }
}

/** API'yi yukler (bir kez). Betik yuklenemez/zaman asarsa reddeder; yetki hatasi icin onGoogleMapsAuthFailure. */
export function loadGoogleMaps(apiKey: string, language: string): Promise<GoogleMapsApi> {
  const ready = loadedApi();
  if (ready) {
    warnKeyMismatch(apiKey);
    return Promise.resolve(ready);
  }
  if (pending) {
    warnKeyMismatch(apiKey);
    return pending;
  }

  requestedKey = apiKey;
  pending = new Promise<GoogleMapsApi>((resolve, reject) => {
    const w = win();
    const script = document.createElement('script');
    const fail = (message: string) => {
      window.clearTimeout(timer);
      // Sonraki deneme (ornegin sayfaya geri donus) yeni betik eklesin
      pending = null;
      requestedKey = null;
      script.remove();
      reject(new Error(message));
    };
    const timer = window.setTimeout(
      () => fail('Google Maps API zaman asimina ugradi (main.js yuklenmedi)'),
      GOOGLE_MAPS_LOAD_TIMEOUT_MS,
    );

    w[CALLBACK_NAME] = () => {
      window.clearTimeout(timer);
      const api = loadedApi();
      if (api) resolve(api);
      else reject(new Error('Google Maps API yuklendi ama beklenen siniflar bulunamadi'));
    };

    const params = new URLSearchParams({
      key: apiKey,
      v: 'weekly',
      language,
      region: 'TR',
      loading: 'async',
      callback: CALLBACK_NAME,
    });
    script.src = `${GOOGLE_MAPS_SCRIPT_HOST}?${params.toString()}`;
    script.async = true;
    script.onerror = () => fail('Google Maps API betigi yuklenemedi');
    document.head.appendChild(script);
  });

  return pending;
}

/** Daha once yetki hatasi alindi mi (anahtar gecersiz, kisitli veya faturalandirma kapali) */
export function hasGoogleMapsAuthFailed(): boolean {
  return authFailed;
}

/** Yetki hatasini dinler; donen fonksiyon aboneligi kaldirir. */
export function onGoogleMapsAuthFailure(listener: () => void): () => void {
  authFailureListeners.add(listener);
  if (!authHookInstalled) {
    authHookInstalled = true;
    const w = win();
    const previous = w.gm_authFailure;
    w.gm_authFailure = () => {
      authFailed = true;
      previous?.();
      authFailureListeners.forEach((fn) => fn());
    };
  }
  return () => {
    authFailureListeners.delete(listener);
  };
}

/** Testler icin: modul durumunu ve window kancalarini sifirlar. */
export function resetGoogleMapsForTests() {
  pending = null;
  requestedKey = null;
  authFailed = false;
  authHookInstalled = false;
  authFailureListeners.clear();
  const w = win();
  delete w.gm_authFailure;
  delete w[CALLBACK_NAME];
  document.head
    .querySelectorAll(`script[src^="${GOOGLE_MAPS_SCRIPT_HOST}"]`)
    .forEach((script) => script.remove());
}
